/**
 * @license SPDX-License-Identifier: Apache-2.0
 * NotebookPageEditor — full-width rich text editor wrapper for the Notebooks tab.
 * Uses DesignStudioEditor as its inner core for native BlockSuite tools and slash commands.
 */

import React, { useState } from 'react';
import { ArrowLeft, Minimize2, Maximize2, Palette } from 'lucide-react';
import { DESK_THEMES } from '../types';
import { DesignStudioEditor } from './DesignStudioEditor';interface NotebookPageEditorProps {
  notebookId: string;
  notebookTitle: string;
  onBack: () => void;
  isPagesSidebarCollapsed?: boolean;
  setIsPagesSidebarCollapsed?: (collapsed: boolean) => void;
  activeCardId?: string;
  activeCardTitle?: string;
  onTitleChange?: (newTitle: string) => void;
}

export const NotebookPageEditor: React.FC<NotebookPageEditorProps> = ({ 
  notebookId, 
  notebookTitle, 
  onBack,
  isPagesSidebarCollapsed = false,
  setIsPagesSidebarCollapsed,
  activeCardId,
  activeCardTitle,
  onTitleChange
}) => {
  const sk = 'nb_pg_' + notebookId;
  const [pageTitle, setPageTitle] = useState(() => {
    try {
      return localStorage.getItem(sk + '_title') || notebookTitle;
    } catch {
      return notebookTitle;
    }
  });

  React.useEffect(() => {
    if (activeCardTitle !== undefined) {
      setPageTitle(activeCardTitle || '');
    } else {
      setPageTitle(notebookTitle);
    }
  }, [activeCardId, activeCardTitle, notebookTitle]);

  const [showColors, setShowColors] = useState(false);
  const isFullscreen = isPagesSidebarCollapsed;
  const setIsFullscreen = (val: boolean) => {
    if (setIsPagesSidebarCollapsed) {
      setIsPagesSidebarCollapsed(val);
    }
  };

  const [deskThemeIdx, setDeskThemeIdx] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('nb_theme_idx_' + notebookId);
      return saved ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  });

  const activeTheme = DESK_THEMES[deskThemeIdx] || DESK_THEMES[0];

  const persistTitle = (t: string) => {
    try {
      localStorage.setItem(sk + '_title', t);
    } catch {}
    if (onTitleChange) {
      onTitleChange(t);
    }
  };

  return (
    <div 
      className="flex-1 flex flex-col h-full overflow-hidden relative transition-all duration-300 font-sans shadow-inner animate-in fade-in"
      style={{ 
        backgroundColor: activeTheme.bgValue, 
        color: activeTheme.textValue,
        fontFamily: '"Inter", system-ui, sans-serif',
      }}
    >
      <div 
        className="flex-1 flex flex-col max-w-7xl mx-auto w-full border-x bg-transparent relative"
        style={{ borderColor: activeTheme.accentBorder || 'rgba(0,0,0,0.1)' }}
      >
        {/* TOP BAR */}
        <div 
          className="w-full border-b shrink-0 px-4 py-3 flex items-center justify-between gap-4 bg-transparent"
          style={{ borderColor: activeTheme.accentBorder || 'rgba(0,0,0,0.1)' }}
        >
          <button 
            onClick={onBack}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold transition-all cursor-pointer select-none"
            style={{ 
              borderColor: activeTheme.accentBorder || 'rgba(0,0,0,0.1)',
              backgroundColor: activeTheme.buttonHoverBg || 'rgba(0,0,0,0.05)',
              color: activeTheme.textValue 
            }}
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Library
          </button>
          <div className="flex-1 min-w-0">
            <input 
              type="text" 
              value={pageTitle}
              onChange={e => { setPageTitle(e.target.value); persistTitle(e.target.value); }}
              placeholder="Untitled Page"
              className="w-full text-lg font-bold bg-transparent border-none outline-none placeholder-black/30 leading-tight"
              style={{ color: activeTheme.textValue }}
            />
          </div>
          <span className="text-[10px] font-mono shrink-0 hidden sm:block" style={{ color: activeTheme.textValue, opacity: 0.6 }}>Auto-saved</span>

          {/* COLOR AND FULLSCREEN ACTIONS */}
          <div className="flex items-center gap-2">
            {/* Color picker */}
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setShowColors(v => !v); }}
                className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer flex items-center justify-center border border-slate-200 text-slate-500 bg-white dark:bg-slate-900"
                title="Desk colors"
              >
                <Palette className="w-3.5 h-3.5" />
              </button>
              {showColors && (
                <div className="absolute right-0 top-full mt-1.5 z-50 bg-white border border-slate-250 rounded-2xl w-[270px] p-4 shadow-2xl block animate-in duration-100">
                  <div className="flex items-center justify-between pb-2 border-b mb-3 text-xs font-bold text-slate-850">
                    <span>Desk Colors</span>
                    <button onClick={() => setShowColors(false)} className="text-indigo-600 hover:underline">Done</button>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {DESK_THEMES.map((th, i) => (
                      <button
                        key={th.name}
                        onClick={() => {
                          setDeskThemeIdx(i);
                          try { localStorage.setItem('nb_theme_idx_' + notebookId, i.toString()); } catch {}
                        }}
                        className={`aspect-video rounded-lg border border-slate-200 hover:border-slate-350 text-[9.5px] font-extrabold flex items-center justify-center p-1 transition-all ${deskThemeIdx === i ? 'border-indigo-650 scale-105 shadow-xs' : 'border-slate-200'}`}
                        style={{ backgroundColor: th.bgValue, color: th.textValue }}
                      >
                        {deskThemeIdx === i && "✓"}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Minimize / Maximize */}
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer flex items-center justify-center border border-slate-200 text-slate-500 bg-white dark:bg-slate-900"
              title={isFullscreen ? "Exit distraction-free mode" : "Distraction-free focus full width"}
            >
              {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>

        {/* EDITOR AREA */}
        <div className="flex-1 overflow-hidden relative bg-transparent">
          <DesignStudioEditor mode="page" docId={activeCardId ? ("doc:notebook-card-" + activeCardId) : ("doc:notebook-" + notebookId)} />
        </div>
      </div>
    </div>
  );
};